/**
 * Property Management Page
 * Manage properties, buildings, and units within HOA/Condo associations
 * Bulk enrollment workflows and property-level service tracking
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import { Building2, Home, MapPin, Plus, Search, Filter, Download } from 'lucide-react';

interface Property {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  property_type: string;
  building_count: number;
  unit_count: number;
  association_id: string;
  status: string;
  created_at: string;
}

interface Building {
  id: string;
  name: string;
  property_id: string;
  unit_count: number;
  floors: number;
  amenities: string[];
  status: string;
}

interface Unit {
  id: string;
  unit_number: string;
  building_id: string;
  square_footage: number;
  bedrooms: number;
  bathrooms: number;
  monthly_fee: number;
  owner_name: string;
  owner_email: string;
  status: string;
}

export default function PropertyManagementPage() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [selectedBuilding, setSelectedBuilding] = useState<Building | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'properties' | 'buildings' | 'units'>('properties');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    setLoading(true);

    const { data: propertiesData } = await supabase
      .from('properties')
      .select('*')
      .order('name', { ascending: true });

    const { data: buildingsData } = await supabase
      .from('buildings')
      .select('*')
      .order('name', { ascending: true });

    const { data: unitsData } = await supabase
      .from('units')
      .select('*')
      .order('unit_number', { ascending: true });

    setProperties(propertiesData || []);
    setBuildings(buildingsData || []);
    setUnits(unitsData || []);
    setLoading(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'maintenance': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const filteredProperties = properties.filter(property =>
    property.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
    property.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPropertyStats = () => {
    const totalUnits = units.length;
    const occupiedUnits = units.filter(u => u.status === 'occupied').length;
    const vacantUnits = units.filter(u => u.status === 'vacant').length;
    const maintenanceUnits = units.filter(u => u.status === 'maintenance').length;

    return { totalUnits, occupiedUnits, vacantUnits, maintenanceUnits };
  };

  const stats = getPropertyStats();

  return (
    <AdminPageLayout
      title="Property Management"
      subtitle="Manage properties, buildings, and units across all associations"
      actions={
        <div className="flex gap-2">
          <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Property
          </button>
        </div>
      }
    >
      {/* Summary Cards */}
      <StatsGrid cols={4}>
        <StatCard
          label="Total Properties"
          value={properties.length}
          icon={Building2}
          subtitle={`${buildings.length} buildings`}
        />
        <StatCard
          label="Total Units"
          value={stats.totalUnits}
          icon={Home}
          subtitle={`${stats.occupiedUnits} occupied`}
        />
        <StatCard
          label="Occupancy Rate"
          value={`${stats.totalUnits > 0 ? Math.round((stats.occupiedUnits / stats.totalUnits) * 100) : 0}%`}
          icon={MapPin}
          subtitle={`${stats.vacantUnits} vacant`}
        />
        <StatCard
          label="Maintenance"
          value={stats.maintenanceUnits}
          icon={Plus}
          subtitle="units in maintenance"
        />
      </StatsGrid>

      {/* Search and Filter Bar */}
      <Card className="p-4 mb-6">
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search properties, addresses, or cities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option>All Types</option>
              <option>Condo</option>
              <option>HOA</option>
              <option>Apartment</option>
              <option>Townhouse</option>
            </select>
            <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option>All Status</option>
              <option>Active</option>
              <option>Maintenance</option>
              <option>Inactive</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6">
        {[
          { id: 'properties', label: 'Properties', count: properties.length },
          { id: 'buildings', label: 'Buildings', count: buildings.length },
          { id: 'units', label: 'Units', count: units.length }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
              activeTab === tab.id
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.label}
            <span className={`px-2 py-1 text-xs rounded-full ${
              activeTab === tab.id
                ? 'bg-blue-200 text-blue-700'
                : 'bg-gray-200 text-gray-600'
            }`}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* Properties Tab */}
      {activeTab === 'properties' && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Properties</h2>
          {loading ? (
            <div className="py-8 text-center text-gray-500">Loading...</div>
          ) : filteredProperties.length === 0 ? (
            <div className="py-8 text-center text-gray-400">
              No properties found. Click "Add Property" to get started.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProperties.map((property) => (
                <div key={property.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-gray-900">{property.name}</h3>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(property.status)}`}>
                      {property.status}
                    </span>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600 mb-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span>{property.address}, {property.city}, {property.state} {property.zip_code}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Type: {property.property_type}</span>
                      <span>{property.unit_count} units</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>{property.building_count} buildings</span>
                      <span>Since {new Date(property.created_at).getFullYear()}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedProperty(property)}
                      className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm font-medium"
                    >
                      View Details
                    </button>
                    <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm font-medium">
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Buildings Tab */}
      {activeTab === 'buildings' && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Buildings</h2>
          {buildings.length === 0 ? (
            <div className="py-8 text-center text-gray-400">
              No buildings found. Add properties first to create buildings.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Building</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Property</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Units</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Floors</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {buildings.map((building) => (
                    <tr key={building.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{building.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {properties.find(p => p.id === building.property_id)?.name || 'Unknown'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{building.unit_count}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{building.floors}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(building.status)}`}>
                          {building.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => setSelectedBuilding(building)}
                          className="text-blue-600 hover:text-blue-800 font-medium mr-3"
                        >
                          View
                        </button>
                        <button className="text-gray-600 hover:text-gray-800 font-medium">
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Units Tab */}
      {activeTab === 'units' && (
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Units</h2>
          {units.length === 0 ? (
            <div className="py-8 text-center text-gray-400">
              No units found. Add buildings first to create units.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Building</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bed/Bath</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monthly Fee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Owner</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {units.map((unit) => (
                    <tr key={unit.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{unit.unit_number}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">
                          {buildings.find(b => b.id === unit.building_id)?.name || 'Unknown'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{unit.square_footage?.toLocaleString()} sq ft</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{unit.bedrooms}/{unit.bathrooms}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">${unit.monthly_fee}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{unit.owner_name}</div>
                        <div className="text-sm text-gray-600">{unit.owner_email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(unit.status)}`}>
                          {unit.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button className="text-blue-600 hover:text-blue-800 font-medium mr-3">
                          View
                        </button>
                        <button className="text-gray-600 hover:text-gray-800 font-medium">
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Property Detail Modal */}
      {selectedProperty && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">{selectedProperty.name}</h2>
              <button
                onClick={() => setSelectedProperty(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Property Details</h3>
                  <dl className="space-y-2 text-sm">
                    <div>
                      <dt className="text-gray-600">Address</dt>
                      <dd className="font-medium">{selectedProperty.address}, {selectedProperty.city}, {selectedProperty.state} {selectedProperty.zip_code}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Type</dt>
                      <dd className="font-medium">{selectedProperty.property_type}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Buildings</dt>
                      <dd className="font-medium">{selectedProperty.building_count}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-600">Total Units</dt>
                      <dd className="font-medium">{selectedProperty.unit_count}</dd>
                    </div>
                  </dl>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Quick Actions</h3>
                  <div className="space-y-2">
                    <button className="w-full px-4 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-left">
                      🏢 Manage Buildings
                    </button>
                    <button className="w-full px-4 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 text-left">
                      🏠 Bulk Unit Enrollment
                    </button>
                    <button className="w-full px-4 py-2 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 text-left">
                      📋 Service History
                    </button>
                    <button className="w-full px-4 py-2 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 text-left">
                      📊 Generate Report
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
}
